import type { SourceId } from '../../../../constants';
import type { DataServiceResponseItem } from '../../../types';
import type { DataValue, PictureValueType } from '../../sources';
import { formatText } from '../formatText';
import { getMediaWithProxy } from '../getMediaWithProxy';
import type { DataServiceApiResponse } from '../handleResponseError';
import { handleResponseError } from '../handleResponseError';
import { getDataServicePostMedia } from './getDataServicePostMedia';

type DataServiceResponse = DataServiceApiResponse<
  DataServiceResponseItem<SourceId, Record<string, unknown> | null>[]
>;

type GetDataServicePostSelectorParams = {
  platform: SourceId;
  domain?: string;
  shouldUseUsername?: boolean;
  shouldUseVideoProxy?: boolean;
  widgetToken?: string;
};

export function getDataServicePostsSelector({
  platform,
  domain = `${platform}.com`,
  shouldUseUsername = false,
  shouldUseVideoProxy = false,
  widgetToken = ''
}: GetDataServicePostSelectorParams) {
  return <T extends DataServiceResponse>(
    response: T
  ): DataValue<PictureValueType[] | string>[] => {
    handleResponseError<DataServiceApiResponse>(response, platform, 'payload');

    return response.payload.map(
      ({
        vendorId: id,
        caption,
        link,
        publishedAt,
        author,
        likesCount,
        commentsCount,
        media: rawMedia
      }) => {
        const text = formatText(caption, platform);
        const media = getDataServicePostMedia({
          attachments: rawMedia,
          platform,
          shouldUseVideoProxy,
          widgetToken
        });
        const {
          name,
          username,
          profilePictureUrl,
          isVerifiedProfile,
          url: userUrl
        } = author ?? {};
        const userIdentifierKey = shouldUseUsername ? 'username' : 'name';
        const userIdentifier = shouldUseUsername ? username : name;

        return {
          id,
          text,
          media,
          timestamp: Date.parse(publishedAt),
          source: {
            type: platform,
            url: link
          },
          user: {
            [userIdentifierKey]: userIdentifier,
            image: getMediaWithProxy(profilePictureUrl),
            verified: isVerifiedProfile,
            url: userUrl ?? `https://${domain}/${username}`
          },
          counters: {
            likes: likesCount,
            comments: commentsCount
          }
        };
      }
    );
  };
}
